/**
 * NoteDetail — one note, full view (My Notes rebuild, Stage 6)
 *
 * Full title, priority chip, dates, the body at a comfortable reading size.
 * Edit is inline (saves through PUT); Delete has a confirm step — the one
 * destructive action in this tool. StickyAskBar here too, scoped to this
 * specific note, so the user can ask JAI about what they wrote.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useLeagues } from '../context/LeagueContext'
import AppShell from '../components/AppShell'
import ToolNav from '../components/ToolNav'
import ToolHeader from '../components/ToolHeader'
import StickyAskBar from '../components/StickyAskBar'
import PriorityChip from '../components/PriorityChip'
import SourceTag from '../components/SourceTag'
import { JAI_FONTS } from '../components/JaiBrand'
import { V2, V2Card, PrimaryBtn, SecondaryBtn } from '../components/JaiV2'
import { JAIMascot } from '../components/JAIMascot'
import { getNote, updateNote, deleteNote } from '../services/notesStore'
import { NOTE_PRIORITIES } from '../config/notePriorities'
import { formatDateTime } from '../utils/datetime'

const focusRingProps = {
  onFocus: (e) => {
    e.currentTarget.style.borderColor = '#3A7CFF'
    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(58,124,255,0.12), 0 1px 4px rgba(10,31,68,0.04)'
  },
  onBlur: (e) => {
    e.currentTarget.style.borderColor = V2.hair
    e.currentTarget.style.boxShadow = '0 1px 4px rgba(10,31,68,0.04)'
  },
}
const fieldStyle = {
  width: '100%', background: 'white', border: `1px solid ${V2.hair}`,
  borderRadius: 13, padding: '13px 16px', fontSize: 13.5, color: V2.navy,
  outline: 'none', boxShadow: '0 1px 4px rgba(10,31,68,0.04)',
  fontFamily: JAI_FONTS.tight, transition: 'border-color 180ms, box-shadow 180ms',
  boxSizing: 'border-box',
}

export default function NoteDetail() {
  const { id: leagueId, noteId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { leagues } = useLeagues()
  const league = leagues.find(l => l.id === Number(leagueId))

  const [note, setNote] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [editing, setEditing] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editBody, setEditBody] = useState('')
  const [editPriority, setEditPriority] = useState('general')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const n = await getNote(leagueId, noteId)
      setNote(n)
    } catch (err) {
      setError(err.response?.status === 404
        ? "This note doesn't exist, or isn't yours."
        : "JAI couldn't load this note right now. Try again in a minute.")
    } finally {
      setLoading(false)
    }
  }, [leagueId, noteId])

  useEffect(() => { load() }, [load])

  const startEdit = () => {
    setEditTitle(note.title)
    setEditBody(note.body)
    setEditPriority(note.priority)
    setSaveError('')
    setEditing(true)
  }

  const saveEdit = async () => {
    if (!editTitle.trim() || !editBody.trim() || saving) return
    setSaving(true)
    setSaveError('')
    try {
      const updated = await updateNote(leagueId, noteId, {
        title: editTitle.trim(), body: editBody.trim(), priority: editPriority,
      })
      setNote(updated)
      setEditing(false)
    } catch {
      setSaveError('Could not save your changes. Try again.')
    } finally {
      setSaving(false)
    }
  }

  const confirmDelete = async () => {
    if (deleting) return
    setDeleting(true)
    setDeleteError('')
    try {
      await deleteNote(leagueId, noteId)
      navigate(`/league/${leagueId}/notes`)
    } catch {
      setDeleteError('Could not delete this note. Try again.')
      setDeleting(false)
    }
  }

  const askBarContext = useMemo(() => {
    if (!note) return undefined
    return `THE USER IS LOOKING AT THIS NOTE RIGHT NOW.\n[${note.priority}] ${note.title}\n${note.body}`
  }, [note])

  return (
    <AppShell>
      <div style={{ padding: '30px 40px', fontFamily: JAI_FONTS.tight }}>
        <div style={{ maxWidth: 640, margin: '0 auto', paddingBottom: 100 }}>
          <ToolNav leagueId={leagueId} />

          <div style={{ marginBottom: 14 }}>
            <Link to={`/league/${leagueId}/notes`} style={{ fontSize: 12.5, fontWeight: 700, color: V2.blue, textDecoration: 'none' }}>
              ← Back to My Notes
            </Link>
          </div>

          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 0', gap: 12 }}>
              <JAIMascot size={48} state="thinking" animate={true} />
              <p style={{ fontSize: 13, color: V2.faint, margin: 0 }}>Loading your note…</p>
            </div>
          )}

          {!loading && error && (
            <V2Card pad="28px 22px" style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 13.5, color: V2.error, margin: '0 0 10px' }}>{error}</p>
              <SecondaryBtn onClick={load}>Try again</SecondaryBtn>
            </V2Card>
          )}

          {!loading && !error && note && (
            <>
              <ToolHeader leagueName={league?.name} title={editing ? 'Edit note' : note.title} subtitle={undefined} />

              <V2Card pad="22px 24px">
                {!editing ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                      <PriorityChip priority={note.priority} size="lg" />
                      <SourceTag source={note.source} />
                      <span style={{ marginLeft: 'auto', fontSize: 11.5, color: V2.faint, fontFamily: JAI_FONTS.mono }}>
                        {note.createdAt === note.updatedAt
                          ? `Written ${formatDateTime(note.createdAt, user?.timezone)}`
                          : `Last edited ${formatDateTime(note.updatedAt, user?.timezone)}`}
                      </span>
                    </div>

                    <div style={{ fontSize: 16, color: V2.navy, lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>
                      {note.body}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 22, paddingTop: 18, borderTop: `1px solid ${V2.hairSoft}` }}>
                      <SecondaryBtn onClick={startEdit}>Edit</SecondaryBtn>
                      {!confirmingDelete ? (
                        <button
                          onClick={() => setConfirmingDelete(true)}
                          style={{ background: 'none', border: 'none', color: V2.error, fontWeight: 700, cursor: 'pointer', fontSize: 13, padding: '8px 4px' }}
                        >
                          Delete
                        </button>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 4 }}>
                          <span style={{ fontSize: 12.5, color: V2.mute }}>Delete this note? This can't be undone.</span>
                          <button
                            onClick={() => setConfirmingDelete(false)}
                            style={{ background: 'none', border: 'none', color: V2.mute, fontWeight: 700, cursor: 'pointer', fontSize: 12.5 }}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={confirmDelete}
                            disabled={deleting}
                            style={{ background: 'none', border: 'none', color: V2.error, fontWeight: 800, cursor: deleting ? 'default' : 'pointer', fontSize: 12.5 }}
                          >
                            {deleting ? 'Deleting…' : 'Yes, delete'}
                          </button>
                        </div>
                      )}
                    </div>
                    {deleteError && <p style={{ fontSize: 12.5, color: V2.error, margin: '10px 0 0' }}>{deleteError}</p>}
                  </>
                ) : (
                  <>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: V2.faint, display: 'block', marginBottom: 6 }}>Title</label>
                      <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} style={fieldStyle} {...focusRingProps} />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: V2.faint, display: 'block', marginBottom: 6 }}>Note</label>
                      <textarea
                        value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={6}
                        style={{ ...fieldStyle, resize: 'vertical', lineHeight: 1.5 }} {...focusRingProps}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: V2.faint, marginRight: 4 }}>Priority:</span>
                      {NOTE_PRIORITIES.map((p) => (
                        <button
                          key={p} onClick={() => setEditPriority(p)}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', opacity: editPriority === p ? 1 : 0.4 }}
                        >
                          <PriorityChip priority={p} />
                        </button>
                      ))}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <PrimaryBtn onClick={saveEdit} disabled={!editTitle.trim() || !editBody.trim() || saving}>
                        {saving ? 'Saving…' : 'Save changes'}
                      </PrimaryBtn>
                      <SecondaryBtn onClick={() => setEditing(false)} disabled={saving}>Cancel</SecondaryBtn>
                    </div>
                    {saveError && <p style={{ fontSize: 12.5, color: V2.error, margin: '10px 0 0' }}>{saveError}</p>}
                  </>
                )}
              </V2Card>
            </>
          )}
        </div>
      </div>

      <StickyAskBar
        leagueId={leagueId}
        scope="notes"
        context={askBarContext}
        placeholder="Ask JAI about this note"
      />
    </AppShell>
  )
}
